/**
 * NotificationBell Component
 * Bell icon with unread badge. Click opens the notification dropdown.
 */

import { OpenParlayContext } from '@contexts/OpenParlayContext';
import { useNotifications } from '@hooks/useNotifications';
import { Bell } from 'lucide-react';
import { type ReactElement, useCallback, useContext, useRef, useState } from 'react';
import { NotificationDropdown } from './NotificationDropdown';

export function NotificationBell(): ReactElement {
  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    dismiss,
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      // Lazy-fetch full list when opening
      fetchNotifications();
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, fetchNotifications]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openParlay = useContext(OpenParlayContext);
  const handleNavigateToParlay = useCallback(
    (parlayId: number) => {
      setIsOpen(false);
      openParlay?.(parlayId);
    },
    [openParlay],
  );

  // Outside-click + Escape live in NotificationDropdown now: it renders in a
  // portal (to escape the sticky header's containing block), so it is no longer
  // a DOM descendant of containerRef and a containerRef-based check would close
  // it on every click inside the dropdown.

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`p-2 rounded-lg bg-theme-surface transition relative min-h-11 min-w-11 flex items-center justify-center ${isOpen ? 'bg-theme-surface-hover text-theme-text' : 'hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text'}`}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center leading-tight">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationDropdown
          notifications={notifications}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onDismiss={dismiss}
          onClose={handleClose}
          onNavigateToParlay={openParlay ? handleNavigateToParlay : undefined}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
}
